import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FERTILITY_DEMO_SECTION_BORDER, linkInline, demoChapterStackColumnStyle } from '../designSystem';
import {
  LG_BTQ_DEMO_UPDATE,
  PATH_IDS,
  communityRooms,
  deriveChecklistRows,
  financialRows,
  getCompletedIdsForPath,
  clearLgbtqPath,
  loadLgbtqChecklistByPath,
  loadLgbtqPath,
  markNextChecklistComplete,
  saveLgbtqPath,
} from '../data/fertilityLgbtqDemo';

const FONT_BODY = "'Plus Jakarta Sans', -apple-system, sans-serif";
const FONT_SERIF = "'Playfair Display', Georgia, serif";

const PATH_CARDS = [
  { id: 'lesbian', emoji: '👩‍❤️‍👩', title: 'Lesbian couple', sub: 'IUI · IVF · Reciprocal IVF' },
  { id: 'gay', emoji: '👨‍❤️‍👨', title: 'Gay couple', sub: 'Egg donor · Gestational carrier' },
  { id: 'trans', emoji: '🌈', title: 'Trans / non-binary', sub: 'Fertility preservation · Family building' },
  { id: 'solo', emoji: '🌟', title: 'Solo journey', sub: 'IUI · IVF · Donor sperm or egg' },
];

function CheckIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" aria-hidden>
      <path d="M2 6l3 3 5-6" stroke="var(--white)" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function FertilityDemoLGBTQ({ m = false, navigate }) {
  const nav = navigate || (() => {});
  const [path, setPath] = useState(() => loadLgbtqPath());
  const [byPath, setByPath] = useState(() => loadLgbtqChecklistByPath());

  const syncFromStorage = useCallback(() => {
    setPath(loadLgbtqPath());
    setByPath(loadLgbtqChecklistByPath());
  }, []);

  useEffect(() => {
    const onUpdate = () => syncFromStorage();
    window.addEventListener(LG_BTQ_DEMO_UPDATE, onUpdate);
    window.addEventListener('storage', onUpdate);
    return () => {
      window.removeEventListener(LG_BTQ_DEMO_UPDATE, onUpdate);
      window.removeEventListener('storage', onUpdate);
    };
  }, [syncFromStorage]);

  const completed = useMemo(() => (path ? getCompletedIdsForPath(path, byPath) : new Set()), [path, byPath]);
  const { currentId, ordered } = useMemo(
    () => (path ? deriveChecklistRows(path, completed) : { currentId: null, ordered: [] }),
    [path, completed],
  );

  const selectPath = (pid) => {
    if (!PATH_IDS.includes(pid)) return;
    if (path === pid) {
      clearLgbtqPath();
      setPath(null);
      setByPath(loadLgbtqChecklistByPath());
      return;
    }
    saveLgbtqPath(pid);
    setPath(pid);
    setByPath(loadLgbtqChecklistByPath());
  };

  const onMarkNext = () => {
    if (!path) return;
    const next = markNextChecklistComplete(path, byPath);
    setByPath(next);
  };

  const fin = path ? financialRows(path) : null;
  const rooms = path ? communityRooms(path) : [];
  const linkPink = { ...linkInline, color: 'var(--pink-d)', fontSize: 12, fontWeight: 600 };
  const demoBase = '/demo/fertility';

  return (
    <div style={{ fontFamily: FONT_BODY, width: '100%', boxSizing: 'border-box', ...demoChapterStackColumnStyle }}>
      {/* Path selector — Nova callout lives on ChapterShellPageHeader only */}
      <div
        style={{
          background: 'var(--card)',
          border: FERTILITY_DEMO_SECTION_BORDER,
          borderRadius: 'var(--r, 14px)',
          padding: '16px 18px',
          boxSizing: 'border-box',
        }}
      >
        <h2
          style={{
            fontFamily: FONT_SERIF,
            fontSize: 16,
            fontWeight: 700,
            color: 'var(--text, #111)',
            lineHeight: 1.25,
            margin: '0 0 6px',
          }}
        >
          Select your chapter
        </h2>
        <p
          style={{
            fontFamily: FONT_BODY,
            fontSize: 12,
            fontWeight: 400,
            color: 'var(--muted)',
            lineHeight: 1.45,
            margin: '0 0 16px',
            maxWidth: 640,
          }}
        >
          Choose the journey that fits your family-building story—the platform tailors your checklist, costs, and rooms
          below. Tap again to clear your selection.
        </p>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: m ? '1fr 1fr' : 'repeat(4, 1fr)',
            gap: 12,
          }}
        >
        {PATH_CARDS.map((c) => {
          const active = path === c.id;
          return (
            <button
              key={c.id}
              type="button"
              aria-pressed={active}
              title={active ? 'Click again to clear selection' : undefined}
              onClick={() => selectPath(c.id)}
              style={{
                textAlign: 'left',
                cursor: 'pointer',
                background: active ? 'var(--pink-l)' : 'var(--white)',
                border: active ? '1.5px solid var(--pink)' : '1.5px solid var(--border)',
                borderRadius: 12,
                padding: '14px 16px',
                fontFamily: 'inherit',
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                boxSizing: 'border-box',
              }}
            >
              <span style={{ fontSize: 22, lineHeight: 1 }} aria-hidden>
                {c.emoji}
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: active ? 'var(--pink-d)' : 'var(--text)' }}>{c.title}</span>
              <span style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.35 }}>{c.sub}</span>
              {active ? (
                <span
                  style={{
                    marginTop: 4,
                    alignSelf: 'flex-start',
                    fontSize: 10,
                    fontWeight: 700,
                    color: 'var(--white)',
                    background: 'var(--pink)',
                    borderRadius: 999,
                    padding: '3px 10px',
                  }}
                >
                  Your path
                </span>
              ) : null}
            </button>
          );
        })}
        </div>
      </div>

      {/* Two columns */}
      <div style={{ display: 'grid', gridTemplateColumns: m ? '1fr' : '1fr 1fr', gap: 14 }}>
        {/* Left — checklist */}
        <div
          style={{
            background: 'var(--card)',
            border: FERTILITY_DEMO_SECTION_BORDER,
            borderRadius: 'var(--r)',
            padding: '16px 18px',
            boxSizing: 'border-box',
          }}
        >
          <div className="card-label">
            <span>Your journey checklist</span>
            <span role="button" tabIndex={0} onClick={() => nav(`${demoBase}/community`)} onKeyDown={(e) => e.key === 'Enter' && nav(`${demoBase}/community`)} style={{ ...linkPink, cursor: 'pointer', textTransform: 'none', letterSpacing: 0, fontSize: 12, fontWeight: 500 }}>
              See all →
            </span>
          </div>

          {!path ? (
            <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.5, margin: 0 }}>Choose a path above to see your personalized checklist.</p>
          ) : (
            <>
              {ordered.map((row, idx) => (
                <div
                  key={row.id}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    padding: '12px 0',
                    borderBottom: idx < ordered.length - 1 ? '0.5px solid var(--sand-2)' : 'none',
                  }}
                >
                  <div
                    style={{
                      width: 18,
                      height: 18,
                      minWidth: 18,
                      borderRadius: 4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      boxSizing: 'border-box',
                      background: row.state === 'done' ? 'var(--pink)' : 'var(--white)',
                      border:
                        row.state === 'done'
                          ? '1.5px solid var(--pink)'
                          : row.state === 'current'
                            ? '1.5px solid var(--pink)'
                            : '1.5px solid var(--border)',
                    }}
                  >
                    {row.state === 'done' ? <CheckIcon /> : null}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: row.state === 'current' ? 600 : 400,
                        color: row.state === 'current' ? 'var(--pink-d)' : 'var(--muted)',
                        textDecoration: row.state === 'done' ? 'line-through' : 'none',
                        lineHeight: 1.4,
                      }}
                    >
                      {row.label}
                    </div>
                    {row.state === 'current' ? (
                      <span
                        style={{
                          display: 'inline-block',
                          marginTop: 6,
                          fontSize: 10,
                          fontWeight: 600,
                          color: 'var(--pink-d)',
                          background: 'var(--pink-l)',
                          borderRadius: 10,
                          padding: '2px 8px',
                        }}
                      >
                        In progress
                      </span>
                    ) : null}
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={onMarkNext}
                style={{
                  marginTop: 14,
                  width: '100%',
                  fontFamily: 'inherit',
                  fontSize: 13,
                  fontWeight: 700,
                  color: 'var(--white)',
                  background: 'var(--pink)',
                  border: 'none',
                  borderRadius: 10,
                  padding: '12px 16px',
                  cursor: 'pointer',
                }}
              >
                + Mark next complete
              </button>
            </>
          )}
        </div>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Financial */}
          <div
            style={{
              background: 'var(--card)',
              border: FERTILITY_DEMO_SECTION_BORDER,
              borderRadius: 'var(--r)',
              padding: '16px 18px',
              boxSizing: 'border-box',
            }}
          >
            <div className="card-label">
              <span>Financial tracker</span>
              <span
                role="button"
                tabIndex={0}
                onClick={() => nav(`${demoBase}/financial`)}
                onKeyDown={(e) => e.key === 'Enter' && nav(`${demoBase}/financial`)}
                style={{ ...linkPink, cursor: 'pointer', textTransform: 'none', letterSpacing: 0, fontSize: 12, fontWeight: 500 }}
              >
                Edit →
              </span>
            </div>
            {!fin ? (
              <p style={{ fontSize: 13, color: 'var(--muted)', margin: 0, lineHeight: 1.5 }}>Select a path to see typical costs for your journey.</p>
            ) : (
              <>
                {fin.rows.map((r, i) => (
                  <div
                    key={r.left}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 12,
                      padding: '10px 0',
                      borderBottom: '0.5px solid var(--sand-2)',
                      fontSize: 13,
                    }}
                  >
                    <span style={{ color: 'var(--text)' }}>{r.left}</span>
                    <span style={{ fontWeight: 700, color: r.mutedPink ? 'var(--pink-d)' : 'var(--text)' }}>{r.right}</span>
                  </div>
                ))}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingTop: 12,
                    marginTop: 4,
                    fontSize: 13,
                  }}
                >
                  <span style={{ color: 'var(--text)' }}>{fin.totalLabel}</span>
                  <span style={{ fontWeight: 700, color: 'var(--pink-d)' }}>{fin.totalValue}</span>
                </div>
              </>
            )}
            <p style={{ fontSize: 10, color: 'var(--muted)', lineHeight: 1.45, margin: '12px 0 0' }}>
              Costs vary by clinic and insurance coverage. Nova can help you research your specific situation.
            </p>
          </div>

          {/* Community */}
          <div
            style={{
              background: 'var(--card)',
              border: FERTILITY_DEMO_SECTION_BORDER,
              borderRadius: 'var(--r)',
              padding: '16px 18px',
              boxSizing: 'border-box',
            }}
          >
            <div className="card-label">
              <span>Your community</span>
              <span role="button" tabIndex={0} onClick={() => nav(`${demoBase}/community`)} onKeyDown={(e) => e.key === 'Enter' && nav(`${demoBase}/community`)} style={{ ...linkPink, cursor: 'pointer', textTransform: 'none', letterSpacing: 0, fontSize: 12, fontWeight: 500 }}>
                See all →
              </span>
            </div>
            {!path ? (
              <p style={{ fontSize: 13, color: 'var(--muted)', margin: 0 }}>Rooms update when you choose a path.</p>
            ) : (
              rooms.map((room, idx) => (
                <div
                  key={room.name}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 0',
                    borderBottom: idx < rooms.length - 1 ? '0.5px solid var(--sand-2)' : 'none',
                  }}
                >
                  <span style={{ fontSize: 13, color: 'var(--text)' }}>{room.name}</span>
                  <span style={{ fontSize: 12, color: room.live ? '#1D9E75' : 'var(--muted)', flexShrink: 0 }}>{room.status}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Clinics */}
      <div
        style={{
          background: 'var(--card)',
          border: FERTILITY_DEMO_SECTION_BORDER,
          borderRadius: 'var(--r)',
          padding: '16px 18px',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ marginBottom: 8 }}>
          <div className="card-label">
            <span>LGBTQ+-affirming clinics near you</span>
            <span role="button" tabIndex={0} onClick={() => nav(`${demoBase}/doctors`)} onKeyDown={(e) => e.key === 'Enter' && nav(`${demoBase}/doctors`)} style={{ ...linkPink, cursor: 'pointer', textTransform: 'none', letterSpacing: 0, fontSize: 12, fontWeight: 500 }}>
              See all →
            </span>
          </div>
          <p style={{ fontSize: 11, color: 'var(--muted)', margin: '0 0 0', lineHeight: 1.45 }}>
            HRC Healthcare Equality Index certified · Matched by location and insurance
          </p>
        </div>

        <div
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'flex-start',
            padding: '14px 0',
            borderBottom: '0.5px solid var(--sand-2)',
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: 'var(--pink-l)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 16,
              flexShrink: 0,
            }}
            aria-hidden
          >
            🏥
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>Ohio Fertility Network</span>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'var(--pink-d)',
                  background: 'var(--pink-l)',
                  borderRadius: 999,
                  padding: '2px 8px',
                }}
              >
                Your clinic
              </span>
            </div>
            <p style={{ fontSize: 11, color: 'var(--muted)', margin: '4px 0 0', lineHeight: 1.45 }}>
              HRC certified · Welcoming · Accepts most insurance · Your current clinic
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '14px 0 0', flexWrap: 'wrap' }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: 'var(--pink-l)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 16,
              flexShrink: 0,
            }}
            aria-hidden
          >
            🏥
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>Shady Grove Fertility</div>
            <p style={{ fontSize: 11, color: 'var(--muted)', margin: '4px 0 0', lineHeight: 1.45 }}>
              HRC certified · LGBTQ+ specialized team · Columbus, Ohio area
            </p>
            <span role="button" tabIndex={0} onClick={() => nav(`${demoBase}/doctors`)} onKeyDown={(e) => e.key === 'Enter' && nav(`${demoBase}/doctors`)} style={{ ...linkPink, fontSize: 11, cursor: 'pointer', display: 'inline-block', marginTop: 6 }}>
              View →
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
